/**
 * Build-time feature flags derived from validated env vars.
 *
 * Kept in its own module (rather than in `lib/constants.ts`) so the widely
 * imported constants file stays free of the `env` import — avoiding any import
 * cycle and keeping env validation localized to the few flag consumers (F07).
 */

import { env } from '@/lib/env'
import { MAKERWORLD_CROWDFUND_URL } from '@/lib/links'

/**
 * Live terrain preview (docs/features/terrain-preview.md). Ships dark: only
 * `'true'` enables it. When false, `terrain.tsx` renders exactly today's UI and
 * emits a byte-identical JobConfig (no preview fetch, no z-scale field, no
 * `z_scale` key).
 */
export const TERRAIN_PREVIEW_ENABLED = env.VITE_TERRAIN_PREVIEW === 'true'

/**
 * Thin-kerf assembled-layout lock for the Bambu export modal. Defaults ON to
 * preserve the historical UX: at a kerf <= KERF_SEPARATE_THRESHOLD_MM the
 * assembled layout is locked off and the separated layout selected (pieces
 * would otherwise fuse). Only the literal `'false'` lifts the lock so any kerf
 * can be printed assembled; unset/anything else keeps it on. This is a
 * frontend-only decision — the backend honors whatever separate_pieces the
 * export request sends and does not re-enforce a thin-kerf override.
 */
export const FORCE_SEPARATE_THIN_KERF_ENABLED = env.VITE_FORCE_SEPARATE_THIN_KERF !== 'false'

/**
 * Where the tier-locked "Upgrade" CTAs point (C-UPGRADE): the MakerWorld
 * crowdfunding campaign for Terrain 6. Single env-driven constant so the URL is
 * a one-line change if the campaign ever moves; no component should read
 * `import.meta.env` directly. Open external links with
 * `target="_blank" rel="noopener noreferrer"`.
 *
 * The default falls back to the SAME canonical literal the marketing pages use
 * (`MAKERWORLD_CROWDFUND_URL` in `lib/links.ts`) so there is exactly one copy of
 * the campaign URL string — no drift between the upsell CTA and the marketing
 * "Back Us" links. `VITE_UPGRADE_URL` still overrides for deploy-time changes.
 *
 * As of C4 the internal upgrade CTAs point at `/pricing`; this constant's only
 * remaining consumers are this file and `flags.test.ts`.
 * Phase 6: retire (stripe-subscriptions plan).
 */
export const UPGRADE_URL = env.VITE_UPGRADE_URL || MAKERWORLD_CROWDFUND_URL

/**
 * Code-optional signup (stripe-subscriptions C5 — UI side of B7's backend
 * ALLOW_OPEN_SIGNUP). Ships dark; Phase 5 (D) flips BOTH flags together — the
 * UI flag without the backend one produces "Access code is required" errors at
 * submit (safe but ugly), the backend one without the UI is simply unreachable
 * from our register page. Only the literal 'true' enables.
 */
export const OPEN_SIGNUP_ENABLED = env.VITE_OPEN_SIGNUP === 'true'

/**
 * Cloudflare Turnstile PUBLIC site key for the open (codeless) signup form.
 *
 * Empty (the local-dev default) means no widget is rendered and the signup
 * carries no token. That is only viable against a pool whose Pre-Signup trigger
 * has no `TURNSTILE_SECRET_ARN` — the deployed trigger FAILS CLOSED, rejecting
 * any codeless signup with no verified token. So: blank here for local dev,
 * always set in `.env.staging` / `.env.production`.
 */
export const TURNSTILE_SITE_KEY = env.VITE_TURNSTILE_SITE_KEY

/**
 * Public maker directory (docs/tickets/find-a-maker). Ships dark: only 'true'
 * mounts the /makers route + the /app/account editor + the /app/admin queue.
 * When false the maker surfaces render nothing and no anonApi call is made.
 */
export const MAKER_DIRECTORY_ENABLED = env.VITE_MAKER_DIRECTORY === 'true'

/**
 * Video creator rewards (docs/tickets/annual-and-puzzle-unlock). Ships dark:
 * only 'true' shows the /pricing rewards blurb, the /app/account submit section
 * and the /app/admin/rewards moderation link + route. When false none of those
 * render and no rewards API call is made.
 */
export const CREATOR_REWARDS_ENABLED = env.VITE_CREATOR_REWARDS === 'true'

/**
 * Share cards (docs/tickets/share-cards). Ships dark: only 'true' shows the
 * Share button on a completed job. When false the share sheet's code is never
 * loaded.
 */
export const SHARE_CARDS_ENABLED = env.VITE_SHARE_CARDS === 'true'

/**
 * My filaments (docs/tickets/owned-filaments). Ships dark: only 'true' shows the
 * filament browser, the account-page card and the picker "My filaments" section
 * + match toggle. When false, pickers, the account page and the job page render
 * exactly today's UI, the catalog chunk is never loaded and no owned-filament
 * request is made.
 */
export const OWNED_FILAMENTS_ENABLED = env.VITE_OWNED_FILAMENTS === 'true'
